<template>
  <div 
    v-if="isDevelopment"
    class="floating-location-debug"
    :style="{ top: position.y + 'px', left: position.x + 'px' }"
    @mousedown="startDrag"
  >
    <div class="debug-header">
      <span class="debug-title">📍 Location Debug</span>
      <button @click="toggleMinimized" class="btn-minimize">
        {{ isMinimized ? '▲' : '▼' }}
      </button>
    </div>
    
    <div v-show="!isMinimized" class="debug-body">
      <!-- Current Location Display -->
      <div class="current-location">
        <strong>Current Location:</strong>
        <div class="location-info">
          <span>Lat: {{ currentLocation.lat.toFixed(6) }}</span>
          <span>Lng: {{ currentLocation.lng.toFixed(6) }}</span>
          <span>Accuracy: {{ currentLocation.accuracy }}m</span>
        </div>
      </div>

      <!-- Preset Locations -->
      <div class="preset-locations">
        <strong>Quick Presets:</strong>
        <div class="preset-buttons">
          <button 
            v-for="preset in presets" 
            :key="preset.name"
            @click="setLocation(preset)"
            class="btn-preset"
            :class="{ active: isActivePreset(preset) }"
          >
            {{ preset.emoji }} {{ preset.name }}
          </button>
        </div>
      </div>

      <!-- Custom Location Input -->
      <div class="custom-location">
        <strong>Custom Location:</strong>
        <div class="input-group">
          <input 
            v-model.number="customLat" 
            type="number" 
            step="0.000001"
            placeholder="Latitude"
            class="input-small"
          />
          <input 
            v-model.number="customLng" 
            type="number" 
            step="0.000001"
            placeholder="Longitude"
            class="input-small"
          />
          <button @click="setCustomLocation" class="btn-apply">Apply</button>
        </div>
      </div>

      <!-- Use Real Location -->
      <div class="real-location">
        <button @click="useRealLocation" class="btn-real">
          🌍 Use Real GPS
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'FloatingLocationDebug',
  
  data() {
    return {
      isDevelopment: import.meta.env.MODE !== 'production',
      isMinimized: false,
      position: { x: window.innerWidth - 320, y: 100 },
      dragging: false,
      dragOffset: { x: 0, y: 0 },
      customLat: 0,
      customLng: 0,
      presets: [
        { 
          name: 'Tokyo', 
          emoji: '🗾',
          lat: 35.689487, 
          lng: 139.691706, 
          accuracy: 50 
        },
        { 
          name: 'Berlin', 
          emoji: '🇩🇪',
          lat: 52.520008, 
          lng: 13.404954, 
          accuracy: 50 
        },
        { 
          name: 'Cairo', 
          emoji: '🇪🇬',
          lat: 30.044420, 
          lng: 31.235712, 
          accuracy: 50 
        },
        { 
          name: 'New York', 
          emoji: '🗽',
          lat: 40.712776, 
          lng: -74.005974, 
          accuracy: 50 
        },
        { 
          name: 'London', 
          emoji: '🇬🇧',
          lat: 51.507351, 
          lng: -0.127758, 
          accuracy: 50 
        },
        { 
          name: 'Paris', 
          emoji: '🇫🇷',
          lat: 48.856614, 
          lng: 2.352222, 
          accuracy: 50 
        }
      ]
    };
  },

  computed: {
    ...mapState('app', {
      currentLocation: state => state.position
    })
  },

  methods: {
    setLocation(preset) {
      console.log(`📍 Debug: Setting location to ${preset.name}`);
      this.$store.commit('app/setLocation', {
        lat: preset.lat,
        lng: preset.lng,
        accuracy: preset.accuracy
      });
      this.flash(`Location set to ${preset.name}`, 'success');
    },

    setCustomLocation() {
      if (this.customLat && this.customLng) {
        console.log(`📍 Debug: Setting custom location: ${this.customLat}, ${this.customLng}`);
        this.$store.commit('app/setLocation', {
          lat: this.customLat,
          lng: this.customLng,
          accuracy: 100
        });
        this.flash('Custom location applied', 'success');
      } else {
        this.flash('Please enter valid coordinates', 'error');
      }
    },

    useRealLocation() {
      console.log('📍 Debug: Switching to real GPS location');
      this.$store.dispatch('app/refreshLocation');
      this.flash('Using real GPS location', 'success');
    },

    isActivePreset(preset) {
      return Math.abs(this.currentLocation.lat - preset.lat) < 0.001 &&
             Math.abs(this.currentLocation.lng - preset.lng) < 0.001;
    },

    toggleMinimized() {
      this.isMinimized = !this.isMinimized;
    },

    startDrag(e) {
      if (e.target.closest('.debug-body')) return;
      
      this.dragging = true;
      this.dragOffset.x = e.clientX - this.position.x;
      this.dragOffset.y = e.clientY - this.position.y;
      
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
    },

    onDrag(e) {
      if (!this.dragging) return;
      
      this.position.x = e.clientX - this.dragOffset.x;
      this.position.y = e.clientY - this.dragOffset.y;
    },

    stopDrag() {
      this.dragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    }
  },

  beforeDestroy() {
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
  }
};
</script>

<style scoped>
.floating-location-debug {
  position: fixed;
  z-index: 99999;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  min-width: 300px;
  max-width: 350px;
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  user-select: none;
}

.debug-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: move;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.debug-title {
  font-weight: 600;
  font-size: 14px;
}

.btn-minimize {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s;
}

.btn-minimize:hover {
  background: rgba(255, 255, 255, 0.3);
}

.debug-body {
  padding: 16px;
}

.debug-body > div {
  margin-bottom: 16px;
}

.debug-body > div:last-child {
  margin-bottom: 0;
}

.debug-body strong {
  display: block;
  font-size: 12px;
  margin-bottom: 8px;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.current-location {
  background: rgba(255, 255, 255, 0.1);
  padding: 10px;
  border-radius: 8px;
}

.location-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  font-family: 'Courier New', monospace;
}

.preset-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.btn-preset {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-preset:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
}

.btn-preset.active {
  background: rgba(255, 255, 255, 0.35);
  border-color: rgba(255, 255, 255, 0.5);
  font-weight: 600;
}

.input-group {
  display: flex;
  gap: 6px;
}

.input-small {
  flex: 1;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 11px;
  outline: none;
}

.input-small::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.input-small:focus {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
}

.btn-apply {
  background: rgba(255, 255, 255, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-apply:hover {
  background: rgba(255, 255, 255, 0.35);
}

.btn-real {
  width: 100%;
  background: rgba(76, 175, 80, 0.3);
  border: 1px solid rgba(76, 175, 80, 0.5);
  color: white;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-real:hover {
  background: rgba(76, 175, 80, 0.5);
  transform: translateY(-1px);
}
</style>
